import { adminService } from "@/api/services/admin.service";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

// Middleware/Auth check can be added here if needed
// For now exposing the basic function

export const getUsersProgressFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async () => {
    // In a real app, we would verify admin role here
    // context.auth.role === 'admin'
    const progress = await adminService.getUsersProgress();

    return {
      status: 200,
      body: progress,
    };
  });

const activitySchema = z.object({
  userId: z.string(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export const getUserDetailedActivityFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(activitySchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Unauthorized");
    }
    const result = await adminService.getUserDetailedActivity(
      data.userId,
      data.limit,
      data.offset,
    );
    return {
      status: 200,
      body: result,
    };
  });

export const getUserCoachingAssignmentFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ userId: z.string() }))
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Unauthorized");
    }
    const { coachingClientService } = await import(
      "@/api/services/coaching-client.service"
    );
    const assignment = await coachingClientService.getUserCoachingAssignment(
      data.userId,
    );
    return {
      status: 200,
      body: assignment,
    };
  });

export const resetUserProgressFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(activitySchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Unauthorized");
    }
    const result = await adminService.resetUserProgress(data.userId);
    return {
      status: 200,
      body: result,
    };
  });
/* ── Coaching Goals ────────────────────────────────────── */
const updateGoalsSchema = z.object({
  userId: z.string(),
  goals: z.any(), // Will be validated by insertClientGoalSchema in service logic
});

export const updateClientGoalsFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(updateGoalsSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Unauthorized");
    }
    const { clientGoalService } = await import(
      "@/api/services/client-goal.service"
    );
    const result = await clientGoalService.setClientGoals(
      data.userId,
      context.auth.userId,
      data.goals,
    );
    return {
      status: 200,
      body: result,
    };
  });

const updateAssignmentSchema = z.object({
  userId: z.string(),
  data: z.object({
    startDate: z.string().optional(),
    endDate: z.string().nullable().optional(),
    status: z.any().optional(),
    notes: z.string().nullable().optional(),
  }),
});

export const updateCoachingAssignmentFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(updateAssignmentSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Unauthorized");
    }
    const { coachingClientService } = await import(
      "@/api/services/coaching-client.service"
    );
    const result = await coachingClientService.updateAssignment(data.userId, {
      ...data.data,
      startDate: data.data.startDate
        ? new Date(data.data.startDate)
        : undefined,
      endDate: data.data.endDate
        ? new Date(data.data.endDate)
        : data.data.endDate === null
          ? null
          : undefined,
    });
    return {
      status: 200,
      body: result,
    };
  });

export const getUserCoachingLogsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ userId: z.string() }))
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Unauthorized");
    }
    const { clientMetricService } = await import(
      "@/api/services/client-metric.service"
    );
    const logs = await clientMetricService.getAllMetrics(data.userId);
    return {
      status: 200,
      body: logs,
    };
  });
export const getClientGoalsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ userId: z.string() }))
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      throw new Error("Unauthorized");
    }
    const { clientGoalService } = await import(
      "@/api/services/client-goal.service"
    );
    const goals = await clientGoalService.getClientGoals(data.userId);
    return {
      status: 200,
      body: { goals },
    };
  });
